import React, { useEffect, useState } from 'react';
import { useGetInternalUsersQuery, useUpdateCampaignMutation } from '../endpoints/accounts.endpoints';
import { Campaign } from '../types/client-management.types';
import { CircleAlert } from 'lucide-react';
import { Company } from 'src/pages/billing/types/billing.types';
import { useGetSKUsQuery } from 'src/redux/billing/billing.endpoints';
import BorderedCompanySelect from 'src/components/form-new/bordered-company-select';
import BorderedSkuSelect from 'src/components/form-new/bordered-sku-select';
import BorderedUserSelect from 'src/components/form-new/bordered-user-select';
import { useDispatch } from 'react-redux';
import notificationService from 'src/services/notification/notification.service';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'src/components/ui/table';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from 'src/components/ui/tooltip';

interface CampaignTableProps {
  campaigns: Campaign[];
  companies: Company[];
  filter?: 'attention_needed' | 'all';
  isRefreshing?: boolean;
  className?: string;
  searchQuery: string;
  onUpdateCampaign?: (campaignId: string, field: string, value: any) => void;
}

const CampaignTable: React.FC<CampaignTableProps> = ({
  isRefreshing,
  className,
  filter = 'all',
  campaigns,
  companies,
  searchQuery,
  onUpdateCampaign,
}) => {
  const [filteredCampaigns, setFilteredCampaigns] = useState<Campaign[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useDispatch();

  const [updateCampaign, { isLoading: isUpdating }] = useUpdateCampaignMutation();
  const { data: skus = [] } = useGetSKUsQuery();
  const { data: users = [] } = useGetInternalUsersQuery();

  useEffect(() => {
    setIsLoading(isRefreshing || isUpdating);
  }, [isRefreshing, isUpdating]);

  useEffect(() => {
    setFilteredCampaigns(campaigns.filter((campaign: Campaign) => {
      if (filter === 'attention_needed') {
        if (searchQuery.trim()) {
          return campaign.attention_needed && (campaign?.title?.toLowerCase().includes(searchQuery.toLowerCase()) || campaign?.account?.source?.toLowerCase().includes(searchQuery.toLowerCase()));
        }
        return campaign.attention_needed;
      }

      if (searchQuery.trim()) {
        return campaign?.title?.toLowerCase().includes(searchQuery.toLowerCase()) || campaign?.account?.source?.toLowerCase().includes(searchQuery.toLowerCase());
      }

      return true;
    }));
  }, [filter, campaigns, searchQuery]);

  const handleUpdateCampaign = async(campaignId: string, field: string, value: any) => {
    try {
      await updateCampaign({
        campaignId,
        [field]: value,
      }).unwrap();
      onUpdateCampaign?.(campaignId, field, value);
      notificationService.handleSuccess('Campaign updated successfully', dispatch, 'Campaign updated successfully');
    } catch (error) {
      console.error('Failed to update campaign:', error);
      onUpdateCampaign?.(campaignId, field, value);
      notificationService.handleError(error, dispatch, 'Failed to update campaign');
    }
  };

  return (
    <div className={`overflow-y-auto w-full ${className} bg-white rounded-lg border`}>
      <Table className={`table-fixed ${isLoading ? 'opacity-50 pointer-events-none' : ''}`}>
        <TableHeader>
          <TableRow className="border-b border-gray-200">
            <TableHead className="w-10 text-gray-600"> </TableHead>
            <TableHead className="text-gray-600 w-32 max-w-[140px]">Title</TableHead>
            <TableHead className="text-gray-600 w-28 max-w-[120px]">Account</TableHead>
            <TableHead className="text-gray-600 w-28 max-w-[130px]">Company</TableHead>
            <TableHead className="text-gray-600 w-24 max-w-[100px]">SKU</TableHead>
            <TableHead className="text-gray-600 w-28 max-w-[120px]">Owner</TableHead>
            <TableHead className="text-gray-600 w-20 max-w-[72px]">Bill</TableHead>
            <TableHead className="text-gray-600 w-16 max-w-[64px]">Visible</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {filteredCampaigns.map((campaign: Campaign) => (
            <TableRow key={campaign.id} className="border-b border-gray-100 align-top">
              <TableCell className="py-3">
                {campaign.attention_needed ? (
                  <span className="text-red-500" title="Attention needed">
                    <CircleAlert className="size-5" />
                  </span>
                ) : null}
              </TableCell>
              <TableCell className="py-3 font-medium text-gray-900 w-32 max-w-[140px]">
                <TooltipProvider delayDuration={300}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="block truncate cursor-default">{campaign.title}</span>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-sm">
                      {campaign.title}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </TableCell>
              <TableCell className="py-3 text-sm text-gray-600 w-28 max-w-[120px]">
                <TooltipProvider delayDuration={300}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="block truncate cursor-default">
                        {campaign.account.title}
                        {' | '}
                        {campaign.account?.source?.toLocaleLowerCase().replace('_', ' ')}
                      </span>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-sm">
                      {campaign.account.title}
                      {' | '}
                      {campaign.account?.source ?? ''}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </TableCell>
              <TableCell className="py-2 w-28 max-w-[130px]">
                <BorderedCompanySelect
                  companies={companies}
                  value={campaign.company || ''}
                  onChange={(companyId) => handleUpdateCampaign(campaign.id, 'company', companyId)}
                  label=""
                  suggestionQuery={campaign.title}
                  campaignAccountCompanyId={campaign.account?.company || ''}
                  attentionNeeded={campaign.attention_needed}
                  className="min-w-0 max-w-full text-xs"
                />
              </TableCell>
              <TableCell className="py-2 w-24 max-w-[100px]">
                <BorderedSkuSelect
                  skus={skus}
                  value={campaign.sku || ''}
                  onChange={(skuId) => handleUpdateCampaign(campaign.id, 'sku', skuId)}
                  label=""
                  campaignTitle={campaign.title}
                  attentionNeeded={campaign.attention_needed}
                  className="min-w-0 max-w-full text-xs"
                />
              </TableCell>
              <TableCell className="py-2 w-28 max-w-[120px]">
                <BorderedUserSelect
                  users={users}
                  value={campaign.owner || ''}
                  onChange={(userId) => handleUpdateCampaign(campaign.id, 'owner', userId)}
                  label=""
                  attentionNeeded={campaign.attention_needed}
                  className="min-w-0 max-w-full text-xs"
                />
              </TableCell>
              <TableCell className="py-2 w-20 max-w-[72px] text-center align-middle">
                <div className="flex justify-center items-center">
                  <button
                    type="button"
                    onClick={() => handleUpdateCampaign(campaign.id, 'do_not_bill', !campaign.do_not_bill)}
                  className={`
                    inline-flex items-center justify-center text-xs font-medium px-2 pt-0.5 rounded-full border transition-colors text-white
                    ${campaign.do_not_bill
                      ? 'bg-red-501 border-red-501 hover:bg-red-600 hover:border-red-600'
                      : 'bg-green-501 border-green-501 hover:bg-green-600 hover:border-green-600'
                    }
                  `}
                  title={campaign.do_not_bill ? 'Click to enable billing' : 'Click to set do not bill'}
                >
                  {campaign.do_not_bill ? 'Do not bill' : 'Bill'}
                </button>
                </div>
              </TableCell>
              <TableCell className="py-2 w-16 max-w-[64px] text-center align-middle">
                <div className="flex justify-center items-center">
                  <button
                    type="button"
                    onClick={() => handleUpdateCampaign(campaign.id, 'hide', !campaign.hide)}
                    className={`
                      inline-flex items-center justify-center text-xs font-medium px-2 pt-0.5 rounded-full border transition-colors text-white
                      ${campaign.hide
                        ? 'bg-red-501 border-red-501 hover:bg-red-600 hover:border-red-600'
                        : 'bg-green-501 border-green-501 hover:bg-green-600 hover:border-green-600'
                      }
                    `}
                    title={campaign.hide ? 'Click to show campaign' : 'Click to hide campaign'}
                  >
                    {campaign.hide ? 'Hidden' : 'Visible'}
                  </button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

export default CampaignTable;
